import { useState } from 'react';
import { AnalystButton } from '../components/Analyst/AnalystButton';
import { useNavigate } from 'react-router-dom';
import type { Grain, PeriodKey, Source, Specialty, Targeting } from '../api/types.js';
import { KpiStrip } from '../components/KpiStrip/KpiStrip.js';
import {
  CHART_ROW_DATASET,
  ExportButton,
  PENDING_REGION_PARAM,
  soleRegion,
} from '../components/ExportButton/ExportButton.js';
import { LineChart } from '../components/LineChart/LineChart.js';
import { RegionCards } from '../components/RegionCards/RegionCards.js';
import { RegionPills } from '../components/Pills/RegionPills.js';
import { RegionTable } from '../components/RegionTable/RegionTable.js';
import { SegmentedControl } from '../components/SegmentedControl/SegmentedControl.js';
import {
  ErrorBanner,
  NoSnapshotState,
  Skeleton,
  useReservedHeight,
} from '../components/States/States.js';
import { queries } from '../store/queries.js';
import { useQuery } from '../store/StoreProvider.js';
import {
  CARD_SORT_DEFAULT,
  CARD_SORT_OPTIONS,
  DEFAULT_VIEW,
  comboKey,
  showsTrendArrows,
  useChartFilters,
  useTableFilters,
  type CardSortKey,
  type CardSortState,
} from '../state/filters.js';
import { UI_REGIONS, color, featuredPanel, fontSize, radius } from '../theme/tokens.js';
import { Header } from '../shell/Header.js';

/**
 * Regions Overview (FR-024 – FR-035, PRD §6.2, §6.4).
 *
 * **Section order is chart → pills → KPIs → table / cards** (FR-024, PRD §6.2). Not an
 * arbitrary arrangement: the chart poses the question, the pills set the scope, and the table
 * answers it.
 *
 * **The pills are the chart's region control, and the only one** (FR-030, amended 2026-08-28).
 * They replaced a `Chart region` segmented control that listed the same codes beside them
 * while the pills — the more prominent of the two — navigated away instead. Region Detail is
 * now reached from a table row or a card, and from nothing on this panel.
 *
 * **Two panels, and the boundary is what each control governs.** Everything the chart filters
 * move — the chart itself, the region pills, the chart-region selector — shares one card with
 * the KPI strip, which moves with nothing at all (FR-029). The table filters, the period, and
 * the Cards/Table toggle sit in the second card, above the rows they govern. A control in one
 * card that silently redrew the other is exactly the confusion FR-034 is about, so the two
 * filter sets are never rendered in the same box.
 *
 * **Chart filters and table filters are separate state** (FR-034). Changing a chart filter must
 * not move a single number in the KPI strip or the table — and it cannot, because the KPI
 * figures come from an endpoint that does not accept those parameters at all (FR-029).
 *
 * ## Why the controls sit where they do
 *
 * Each panel reads left to right as *narrow → window → present*: *Source · Targeting ·
 * Specialty*, then the time control (grain here, period there), then, on the table, the view.
 * The three narrowing axes come first and in the **same order in both panels**, because they
 * are the same three axes over the same rows — learn the cluster once and you have learned
 * both, and a chart can be checked against a table by comparing two identical rows of controls.
 *
 * The time control is set apart by a wider gap because it is a different kind of thing: Weekly
 * and 12M change how the rows are *cut*, not which rows there are. Four evenly spaced tracks
 * read as four filters, and a reader then goes looking for the rows Weekly removed.
 *
 * The chart's filters ride the legend row rather than a row of their own (`<LineChart
 * controls>`), which is both the blueprint's arrangement and Region Detail's. The same chart
 * with its filters in two different places on two pages is the kind of drift Principle I exists
 * to prevent — and it puts everything that redraws a line, legend toggles included, in one row.
 *
 * Defaults on a fresh load are PRD §6.4's: Source All, Targeting All, Specialty All, grain
 * Monthly, view Table, period 3M (SC-010).
 */

const SOURCE_OPTIONS = [
  { value: 'all' as Source, label: 'All' },
  { value: 'internal' as Source, label: 'Internal' },
];
const TARGETING_OPTIONS = [
  { value: 'all' as Targeting, label: 'All' },
  { value: 'target' as Targeting, label: 'Target' },
];
const SPECIALTY_OPTIONS = [
  { value: 'all' as Specialty, label: 'All' },
  { value: 'nursing' as Specialty, label: 'Nursing' },
  { value: 'allied' as Specialty, label: 'Allied' },
];
/**
 * Finest first, so the track reads left to right as a widening lens — the same order the period
 * track uses beside it.
 *
 * **`Daily` was added 2026-09-10** (owner request). ⚠ It is twelve **days**, not a longer window
 * cut finer: every grain is exactly 12 buckets (`BUCKET_COUNT`), so this control picks the width
 * of a bucket and, with it, how far back the chart reaches — Daily is the last fortnight where
 * Quarterly is the last three years. It needed no server work beyond the grain itself; the
 * endpoint has always stored one document per (scope, grain).
 */
const GRAIN_OPTIONS = [
  { value: 'daily' as Grain, label: 'Daily' },
  { value: 'weekly' as Grain, label: 'Weekly' },
  { value: 'monthly' as Grain, label: 'Monthly' },
  { value: 'quarterly' as Grain, label: 'Quarterly' },
];
/**
 * Shortest window first, so the track reads left to right as a widening lens.
 *
 * **`MTD` is offered here and on Region Detail's by-client breakdown** (owner decision,
 * 2026-08-28; the breakdown followed later the same day). The API and ingest support it
 * everywhere, so adding it to Delivery is still one line in that page's own list.
 *
 * `30d`/`90d` were `1M`/`3M` and are now rolling day windows rather than calendar months — the
 * rename came with the arithmetic, since a `30d` that started on the 1st would have duplicated
 * `MTD` and shown a single day of data on the 1st of a month (PRD §8.4).
 */
const PERIOD_OPTIONS: Array<{ value: PeriodKey; label: string; accessibleLabel: string }> = [
  { value: 'MTD', label: 'MTD', accessibleLabel: 'Period: month to date' },
  { value: '30d', label: '30d', accessibleLabel: 'Period: last 30 days' },
  { value: '90d', label: '90d', accessibleLabel: 'Period: last 90 days' },
  { value: '6M', label: '6M', accessibleLabel: 'Period: 6 months' },
  { value: '12M', label: '12M', accessibleLabel: 'Period: 12 months' },
  { value: '24M', label: '24M', accessibleLabel: 'Period: 24 months' },
  { value: 'YTD', label: 'YTD', accessibleLabel: 'Period: year to date' },
];

/**
 * The two page panels share the blueprint's card chrome (Principle I) — with the featured edge
 * over it (owner request 2026-08-30), so the chart card and the Region Performance card read as
 * this page's two subjects rather than as two of several stacked rectangles. `<RegionTable>`
 * carries the same treatment through its `featured` prop; these two must not diverge.
 */
const PANEL_STYLE = {
  ...featuredPanel,
  borderRadius: radius['6xl'],
} as const;

/**
 * Overview charts the **sum of every region** by default (FR-024).
 *
 * `ALL` is a chart scope, not a region: it has no pill and no detail page, and the API accepts
 * it on the timeseries route alone. Picking regions from the pills narrows the chart to their
 * sum — one of them, or several — and deselecting them all returns here.
 *
 * It sums R1+R2+R3+R4+R5+SPOT, which is **not** a grand total: rows that reached no region
 * are in none of them and so appear in neither. Where attribution is incomplete this sits
 * below the true total, and the header's data-quality indicator is what explains the gap.
 */
const ALL_REGIONS = 'ALL';

export function RegionsOverview() {
  const navigate = useNavigate();
  const chart = useChartFilters();
  const table = useTableFilters();
  const [view, setView] = useState<'table' | 'cards'>(DEFAULT_VIEW);
  /**
   * The regions the chart is showing, **empty meaning all of them** (FR-030a).
   *
   * A list rather than one code: the pills multi-select, and two of them chart as their sum. The
   * empty state is `ALL` rather than "nothing selected" so there is no way to reach a blank
   * chart by deselecting — and it is what the page opens on.
   */
  /**
   * The chart's region scope — empty meaning all of them (FR-030a).
   *
   * ⚠ **Seeded from the address so a Google consent round trip does not clear it** (FR-088). This
   * is plain component state, and consent is a *full page navigation*: the user picks R3, presses
   * Export, goes to Google, and comes back to a freshly mounted page with nothing selected — so
   * the export that was about to resume had no region, and the button that should have shown a
   * link was disabled instead.
   *
   * The initialiser runs **during the first render**, before any effect, which is what makes this
   * safe: `<ExportButton>` strips the same parameter in a mount effect, and effects always run
   * after render. That ordering is a React guarantee, not a coincidence — but it is the reason
   * this must stay a `useState` initialiser and must not become an effect of its own.
   */
  const [chartRegions, setChartRegions] = useState<readonly string[]>(() => {
    const pending = new URLSearchParams(window.location.search).get(PENDING_REGION_PARAM);
    return pending && (UI_REGIONS as readonly string[]).includes(pending) ? [pending] : [];
  });
  /**
   * How the Cards view is ordered (blueprint § region cards). Cards only: the table sorts by
   * clicking a column header, and the cards have no headers to click.
   */
  const [cardSort, setCardSort] = useState<CardSortState>(CARD_SORT_DEFAULT);

  /**
   * The scope the API is asked for: `ALL`, or the selected codes in `UI_REGIONS` order.
   *
   * Canonical, so R3-then-R1 and R1-then-R3 are one request and one cache entry rather than two
   * that expire apart. The server normalises the same way and would collapse a full set back to
   * `ALL` on its own; sending it that way keeps the request and the cache key honest here too.
   */
  const chartScope =
    chartRegions.length === 0 || chartRegions.length === UI_REGIONS.length
      ? ALL_REGIONS
      : UI_REGIONS.filter((code) => chartRegions.includes(code)).join(',');

  /**
   * Both reads go through the client store (`store/dataStore.ts`), which is usually already
   * holding them: the background prefetch fills the Overview for every period and a chart per
   * region on sign-in, so this page normally has no loading state at all.
   */
  const overview = useQuery(queries.overview(table.filters.period));
  const timeseries = useQuery(queries.regionTimeseries(chartScope, chart.filters));

  const envelope = overview.data;
  const data = envelope?.data ?? null;
  const unattributedBooks = envelope?.dataQuality?.unattributedBooks ?? null;
  const combo = comboKey(table.filters);

  /**
   * Whether the table's cells draw their arrows — false on MTD and YTD (PRD §9.5, amended
   * 2026-08-28).
   *
   * The arrow is a fixed 30-day trailing comparison and stays that under every period (FR-054).
   * On the two to-date windows the number beside it is a partial calendar span — two days of
   * activity on the 2nd of a month — so the arrow would read as a verdict on a window it never
   * measured. Withheld there, and only there; the figures themselves stay in every tooltip.
   */
  const trendArrows = showsTrendArrows(table.filters.period);

  /**
   * Hold the table's height while a **period change** refetches it.
   *
   * The period control sits in that card's own header, so the section under it used to collapse
   * from a full table to a 320px skeleton and spring back — the control a reader had just touched
   * jumping up the page under their pointer. The chart card fixed the same fault structurally
   * (`<LineChart>` reserves its own rows); this one cannot, because the height is the data's:
   * seven regions of table and seven of cards are not the same box.
   */
  const performanceHeight = useReservedHeight(overview.loading && !data);

  /**
   * Toggle a region into or out of the chart; `null` clears back to all regions.
   *
   * Deselecting the last one lands on `[]`, which *is* all of them — so the chart can never be
   * emptied, and the default scope is reachable from every pill rather than from `ALL` alone.
   */
  function toggleChartRegion(regionCode: string | null) {
    setChartRegions((current) => {
      if (regionCode == null) return [];
      return current.includes(regionCode)
        ? current.filter((code) => code !== regionCode)
        : [...current, regionCode];
    });
  }

  /**
   * What the chart card is headed.
   *
   * One region gets its name, because there is room for it and the pill no longer carries it.
   * Several get their codes joined with `+`: the chart is their **sum**, and a title that said
   * "2 regions" would leave which two to be read off the pills.
   */
  const chartTitle = (() => {
    if (chartScope === ALL_REGIONS) return 'All regions';
    const codes = UI_REGIONS.filter((code) => chartRegions.includes(code));
    if (codes.length > 1) return codes.join(' + ');
    const pill = data?.pills.find((p) => p.regionCode === codes[0]);
    return pill ? `${pill.regionCode} · ${pill.regionName}` : (codes[0] ?? 'All regions');
  })();

  /**
   * The second panel's header: title, table filters, view toggle, and the period caveat.
   *
   * Built once and rendered in whichever container the current view provides — the table's own
   * card via `<DataTable header>`, or the standalone bar the cards and loading views use — so
   * the controls cannot drift apart between the three states.
   */
  const performanceHeader = (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            fontSize: fontSize.sectionTitle,
            fontWeight: 700,
            color: color.ink,
            marginRight: 'auto',
          }}
        >
          Region Performance
        </div>

        {/* Source · Targeting · Specialty, the same three in the same order as the chart's.
            They are the same axes over the same rows, so a reader who learns the cluster once
            has learned both — and either row is checkable against the other at a glance. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <SegmentedControl
            label="Table source"
            options={SOURCE_OPTIONS}
            value={table.filters.source}
            onChange={(v) => table.set('source', v)}
          />
          <SegmentedControl
            label="Table targeting"
            options={TARGETING_OPTIONS}
            value={table.filters.targeting}
            onChange={(v) => table.set('targeting', v)}
          />
          <SegmentedControl
            label="Table specialty"
            options={SPECIALTY_OPTIONS}
            value={table.filters.specialty}
            onChange={(v) => table.set('specialty', v)}
          />
        </div>

        {/* The window — the chart's grain slot, and the only control here that re-fetches. */}
        <div style={{ marginLeft: 6 }}>
          <SegmentedControl
            label="Table period"
            options={PERIOD_OPTIONS}
            value={table.filters.period}
            onChange={(v) => table.set('period', v)}
          />
        </div>

        {/* After the filters and set apart: it changes nothing about the numbers, only their
            shape. */}
        <div style={{ marginLeft: 6 }}>
          <SegmentedControl
            label="View"
            options={[
              { value: 'cards' as const, label: 'Cards' },
              { value: 'table' as const, label: 'Table' },
            ]}
            value={view}
            onChange={setView}
          />
        </div>

        {/*
          The whole table, all seven regions, to a Google Sheet (FR-090).

          ⚠ **LAST on the row, after the view toggle** (owner request 2026-09-13). It used to sit
          between the period and the toggle, which put it in a different slot here than in By
          Client's header and both of Delivery's — where it is last — so a reader who had learned
          where the control lives on one table had to hunt for it on the next. The rule is now
          one line long and holds on all four: the export is the rightmost control, because it is
          the only one that leaves the page and everything shaping what it exports is to its left.

          ⚠ **`allRegions`, with a null region code.** On the chart a null means "the reader has
          not narrowed to one region yet" and disables the control; here it means "every region"
          and must enable it. The two are opposite states wearing the same value, which is why the
          flag exists rather than being inferred.

          ⚠ It is cut by the **table's period**, not the chart's grain — this is the card that
          period control belongs to, and a reader who set `30d` and got twelve months would have
          no way to tell which control was ignored.
        */}
        <div style={{ marginLeft: 6 }}>
          <ExportButton
            regionCode={null}
            allRegions
            window={{ period: table.filters.period }}
          />
        </div>
      </div>
    </div>
  );

  /**
   * The Cards view's ordering control — a track of six figures and a direction toggle.
   *
   * Its own row above the cards rather than a seventh control in the panel header, because it
   * governs neither the numbers nor which rows there are: it is the cards' missing column
   * headers, and it disappears with them when the view is Table.
   */
  const cardSortBar = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 14,
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontSize: fontSize.caption,
          fontWeight: 600,
          letterSpacing: '.5px',
          color: color.inkSubtle,
          textTransform: 'uppercase',
        }}
      >
        Sort by
      </span>
      <SegmentedControl
        label="Sort cards by"
        options={CARD_SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        value={cardSort.key}
        onChange={(key: CardSortKey) => setCardSort((current) => ({ ...current, key }))}
      />
      {/* A toggle rather than two options: ascending and descending are one axis, and the
          glyph states which way the list currently runs. `aria-pressed` says the same thing
          for a reader who cannot see the arrow. */}
      <button
        type="button"
        onClick={() =>
          setCardSort((current) => ({
            ...current,
            direction: current.direction === 'desc' ? 'asc' : 'desc',
          }))
        }
        aria-pressed={cardSort.direction === 'desc'}
        aria-label={`Sort direction: ${
          cardSort.direction === 'desc' ? 'descending' : 'ascending'
        } — activate to reverse`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: color.surface,
          border: `1px solid ${color.border}`,
          borderRadius: radius['2xl'],
          padding: '6px 12px',
          cursor: 'pointer',
          fontSize: fontSize.body,
          fontWeight: 600,
          fontFamily: 'inherit',
          color: color.inkBody,
        }}
      >
        <span className="mono" style={{ color: color.nursing }}>
          {cardSort.direction === 'desc' ? '▼' : '▲'}
        </span>
        {cardSort.direction === 'desc' ? 'Descending' : 'Ascending'}
      </button>
    </div>
  );

  /** The same header as its own card, for the two views that have no table card to sit in. */
  const performanceHeaderCard = (
    <div style={{ ...PANEL_STYLE, padding: '14px 20px', marginBottom: 14 }}>
      {performanceHeader}
    </div>
  );

  return (
    <>
      <Header
        actions={<AnalystButton />}
        crumb="CYNET Health Commercial"
        title="Regions"
        asOf={envelope?.asOf ?? null}
        syncedAt={envelope?.syncedAt ?? null}
        syncStatus={envelope?.syncStatus ?? 'no_data'}
        staleness={envelope?.staleness ?? null}
        unattributedBooks={unattributedBooks}
      />

      <div style={{ padding: '22px 30px 60px' }}>
        {overview.error ? (
          <div style={{ marginBottom: 20 }}>
            <ErrorBanner
              message={`Could not load the Regions overview. ${overview.error.message}`}
              onRetry={overview.reload}
            />
          </div>
        ) : null}

        {envelope && envelope.syncStatus === 'no_data' ? (
          <NoSnapshotState />
        ) : (
          <>
            {/* ---- Panel 1: chart, pills, KPI strip ---- */}
            <section
              aria-label="Performance and key figures"
              style={{ ...PANEL_STYLE, marginBottom: 20 }}
            >
              {/* 1a. Chart */}
              <div style={{ padding: '18px 20px 14px' }}>
                <LineChart
                  data={timeseries.data?.data ?? null}
                  /* The bucket windows in the hover bubble end at the sync, not at the as-of
                     day's calendar edge, on the newest bucket (FR-087). */
                  syncedAt={timeseries.data?.syncedAt ?? null}
                  loading={timeseries.loading}
                  title={chartTitle}
                  /* The plot or the same figures as rows, from the two glyphs at the end of the
                     controls row (owner request 2026-09-09). It is the chart's own state and
                     changes nothing about the response — the pills, the KPI strip and the table
                     below are untouched by it (FR-034). */
                  tableView
                  /*
                    The jobs export (FR-088). ⚠ **One region or nothing**: `chartRegions` is empty
                    for "all regions" and may hold several, and exporting seven regions of raw
                    jobs is a ~400,000-row sheet. The button stays visible and disabled in those
                    two cases, with the reason on its panel, because the fix is to press a region
                    pill — which a reader cannot guess from a control that is not there.

                    ⚠ **`chartRegions`, never `chartScope`.** `chartScope` is the request
                    spelling — the STRING `'ALL'` or `'R3,R4'` — so `chartScope.length === 1` asks
                    how many characters it has and is false for every real region code, which
                    left the button permanently disabled. It typechecks either way: a string has
                    `.length` and indexes to `string`, so nothing but the running page catches it.
                    The selection is the array.
                  */
                  actions={
                    <ExportButton
                      regionCode={soleRegion(chartRegions)}
                      window={{ grain: chart.filters.grain }}
                    />
                  }
                  /*
                    The matrix's own export column (FR-093, owner request 2026-09-14) — one
                    control per row, each writing that row's dataset alone.

                    ⚠ **It does NOT replace `actions` above.** That button is the card's: the
                    region, both tabs, reachable from the plot where there are no rows to press.
                    These two are the JOBS and SUBS rows saying what they are made of, and they
                    exist only while the matrix is on screen.

                    ⚠ **Same window as the card's button — the chart's GRAIN, not the table's
                    period.** The rows being exported are the twelve buckets this chart is
                    drawing; cutting them by the Region Performance period below would export a
                    window nothing on this card names.
                  */
                  onExportRow={(key) => {
                    const dataset = CHART_ROW_DATASET[key];
                    // ⚠ `null`, never a disabled control: TOA and OFFERS have no export, and a
                    // dead button in a four-row table reads as a fault rather than an absence.
                    if (!dataset) return null;
                    return (
                      <ExportButton
                        regionCode={soleRegion(chartRegions)}
                        window={{ grain: chart.filters.grain }}
                        dataset={dataset}
                      />
                    );
                  }}
                  controls={
                    <>
                      {/* The three narrowing axes, in the order the table repeats below. */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          flexWrap: 'wrap',
                        }}
                      >
                        <SegmentedControl
                          label="Chart source"
                          options={SOURCE_OPTIONS}
                          value={chart.filters.source}
                          onChange={(v) => chart.set('source', v)}
                        />
                        <SegmentedControl
                          label="Chart targeting"
                          options={TARGETING_OPTIONS}
                          value={chart.filters.targeting}
                          onChange={(v) => chart.set('targeting', v)}
                        />
                        <SegmentedControl
                          label="Chart specialty"
                          options={SPECIALTY_OPTIONS}
                          value={chart.filters.specialty}
                          onChange={(v) => chart.set('specialty', v)}
                        />
                      </div>

                      {/* Grain re-cuts time; it narrows nothing. The wider gap is the only
                          thing saying so — four identical tracks in an even row read as four
                          filters, and a reader then looks for the rows Weekly removed. */}
                      <div style={{ marginLeft: 8 }}>
                        <SegmentedControl
                          label="Chart grain"
                          options={GRAIN_OPTIONS}
                          value={chart.filters.grain}
                          onChange={(v) => chart.set('grain', v)}
                        />
                      </div>
                    </>
                  }
                />

                {timeseries.error ? (
                  <div style={{ marginTop: 12 }}>
                    <ErrorBanner
                      message="Could not load the chart."
                      onRetry={timeseries.reload}
                    />
                  </div>
                ) : null}
              </div>

              {/* 1b. The pills — the chart's region scope, and the only control of it. */}
              <div style={{ padding: '0 20px 16px' }}>
                <RegionPills
                  pills={data?.pills ?? []}
                  loading={overview.loading && !data}
                  selected={chartRegions}
                  onSelect={toggleChartRegion}
                />
              </div>

              {/* 1c. KPI strip. Hairline rather than a second card: it answers to nothing on
                  this page (FR-029), so it is a band of the panel, not a filtered view. */}
              <div
                style={{
                  borderTop: `1px solid ${color.borderSubtle}`,
                  padding: '16px 20px 18px',
                }}
              >
                <KpiStrip
                  kpi={data?.kpi ?? null}
                  unattributedBooks={unattributedBooks}
                  /* The chart's payload, for the Submissions card alone (owner decision
                     2026-09-02): it totals the window the line above is drawing, so it moves
                     with the grain and with every chart filter. Handing over the response
                     rather than a computed figure is what keeps the card and the line reading
                     the same numbers — there is no second request and no second sum. Every
                     other card on the strip ignores it (FR-029). */
                  timeseries={timeseries.data?.data ?? null}
                  loading={overview.loading && !data}
                />
              </div>
            </section>

            {/* ---- Panel 2: table / cards ---- */}
            <section
              aria-label="Region performance"
              ref={performanceHeight.ref}
              style={performanceHeight.style}
            >
              {overview.loading && !data ? (
                <>
                  {performanceHeaderCard}
                  <Skeleton height={320} />
                </>
              ) : view === 'table' ? (
                <RegionTable
                  /*
                    One export control per row (FR-090), cut by the table's own period.

                    ⚠ Passed as a **node**, not a region code: each control owns a streaming
                    progress reading, a consent round trip and a completion dialog, and that state
                    must survive a re-sort of the rows rather than living inside the table.
                  */
                  onExportRegion={(regionCode) => (
                    <ExportButton
                      regionCode={regionCode}
                      window={{ period: table.filters.period }}
                    />
                  )}
                  header={performanceHeader}
                  showTrendArrows={trendArrows}
                  /* Which rows the chart above is drawing (blueprint § region table). It is a
                     read of the chart's scope, not a filter on the table: FR-034 keeps the two
                     filter sets apart, and marking a row moves no figure in it. */
                  selectedRegionCodes={chartRegions}
                  rows={data?.rows ?? []}
                  totals={data?.totalsByCombo?.[combo] ?? data?.totals ?? {}}
                  combo={combo}
                  unattributedBooks={unattributedBooks}
                  onSelectRegion={(regionCode) => navigate(`/regions/${regionCode}`)}
                />
              ) : (
                <>
                  {performanceHeaderCard}
                  {cardSortBar}
                  <RegionCards
                    rows={data?.rows ?? []}
                    combo={combo}
                    sort={cardSort}
                    unattributedBooks={unattributedBooks}
                    onSelectRegion={(regionCode) => navigate(`/regions/${regionCode}`)}
                  />
                </>
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}
